/**
 * Local User Profile Utility for Flash-Read
 * Provides IndexedDB storage for user profile data (XP, level, streak, tier)
 * Follows patterns from utils/indexedDB.ts
 */

import { initDB, STORES } from './indexedDB';

// ============================================================================
// Type Definitions
// ============================================================================

export interface UserProfile {
  userId: string; // Key path for the store
  membershipTier: 'guest' | 'starter' | 'reader' | 'ultra';
  totalXp: number;
  currentLevel: number;
  lifetimeXp: number;
  currentStreak: number;
  longestStreak: number;
  lastActiveDate: string; // ISO 8601 date string
  tierExpiresAt: string | null; // ISO 8601 date string or null
  freezesAvailable: number;
  freezesUsed: number;
  booksCompleted: number;
  streakMultiplier: number;
  challengesCompleted: number;
  dailyChallengeStreak: number;
  lastModified?: string; // Timestamp for sync conflict resolution
}

// ============================================================================
// Database Operations
// ============================================================================

/**
 * Save or update user profile in IndexedDB
 */
export async function saveUserProfile(profile: UserProfile): Promise<void> {
  const db = await initDB();
  const profileWithTimestamp = {
    ...profile,
    lastModified: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PROFILE], 'readwrite');
    const store = transaction.objectStore(STORES.USER_PROFILE);
    const request = store.put(profileWithTimestamp);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to save user profile: ${request.error?.message}`));
  });
}

/**
 * Get user profile by userId
 */
export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PROFILE], 'readonly');
    const store = transaction.objectStore(STORES.USER_PROFILE);
    const request = store.get(userId);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Failed to get user profile: ${request.error?.message}`));
  });
}

/**
 * Update specific fields of the user profile
 */
export async function updateUserProfile(
  userId: string,
  updates: Partial<Omit<UserProfile, 'userId'>>
): Promise<void> {
  const existing = await getUserProfile(userId);
  if (!existing) {
    throw new Error(`User profile not found for userId: ${userId}`);
  }

  const updatedProfile: UserProfile = {
    ...existing,
    ...updates,
    lastModified: new Date().toISOString(),
  };

  return saveUserProfile(updatedProfile);
}

/**
 * Delete user profile (for logout/cleanup)
 */
export async function deleteUserProfile(userId: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PROFILE], 'readwrite');
    const store = transaction.objectStore(STORES.USER_PROFILE);
    const request = store.delete(userId);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to delete user profile: ${request.error?.message}`));
  });
}

/**
 * Get all user profiles (for admin/debug purposes)
 */
export async function getAllUserProfiles(): Promise<UserProfile[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PROFILE], 'readonly');
    const store = transaction.objectStore(STORES.USER_PROFILE);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(new Error(`Failed to get all user profiles: ${request.error?.message}`));
  });
}
